<template>
    <picture :class="$attrs.class" class="relative">
        <source v-for="item of generateSource()" :key="item.media" :media="item?.media" :srcset="item?.srcset" />
        <img ref="imgRef" v-bind="inputAttrs" class="absolute inset-0 h-full w-full" :class="props.imageClass" />
        <ClientOnly>
            
        </ClientOnly>
    </picture>
</template>

<script lang="ts" setup>
import { joinURL } from 'ufo';
import type { AirImgProps, AirImgVariant } from '~/types/image';

defineOptions({
    inheritAttrs: false
});

const attrs = useAttrs();

const inputAttrs = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const returnObj: { [key: string]: any } = {};
    // use const below
    for (const attr in attrs) {
        if (attr !== 'class') {
            returnObj[attr] = attrs[attr];
        }
    }
    return returnObj;
});

const props = withDefaults(defineProps<AirImgProps>(), {
    imageClass: 'object-cover object-center',
    debug: false,
    quality: 70,
    dpr: () => [1],
    variant: () => [],
    format: 'auto',
    fit: 'crop',
    sharpen: 0.5,
    smBkp: '640px',
    mdBkp: '768px',
    lgBkp: '1024px',
    xlBkp: '1280px',
    xxlBkp: '1536px'
});
let getInfo: unknown;

if (import.meta.dev && props.debug && import.meta.client) {
    const breakpoints = [
        { bkp: 'xxl', val: '(min-width:' + props.xxlBkp + ')' },
        { bkp: 'xl', val: '(min-width:' + props.xlBkp + ')' },
        { bkp: 'lg', val: '(min-width:' + props.lgBkp + ')' },
        { bkp: 'md', val: '(min-width:' + props.mdBkp + ')' },
        { bkp: 'sm', val: '(min-width:' + props.smBkp + ')' },
        { bkp: 'default', val: 'not all and (min-width:' + props.smBkp + ')' }
    ];
    const imgElem = useTemplateRef('imgRef');
    const imgSize = ref({
        W: imgElem.value?.naturalWidth || 0,
        H: imgElem.value?.naturalHeight || 0,
        rw: 0,
        rh: 0
    });
    const debugEl = useTemplateRef('debugEl');

    const windowWidth = ref<number>();
    const getWindowSize = () => {
        windowWidth.value = window.innerWidth;
    };

    onMounted(async () => {
        window.addEventListener('resize', getWindowSize);
        if (imgElem.value)
            imgElem.value.onload = async () => {
                imgSize.value.W = imgElem.value?.naturalWidth || 0;
                imgSize.value.H = imgElem.value?.naturalHeight || 0;
                getRealSize(imgElem.value?.currentSrc || '');
            };

        imgSize.value.W = imgElem.value?.width || 0;
        imgSize.value.H = imgElem.value?.naturalHeight || 0;
        getRealSize(imgElem.value?.currentSrc || '');
    });

    function getRealSize(src: string) {
        const realImage = new Image();
        realImage.onload = () => {
            imgSize.value.rw = realImage.width;
            imgSize.value.rh = realImage.height;
            realImage.onload = null;
        };
        realImage.src = src;
    }

    onUnmounted(() => {
        window.removeEventListener('resize', getWindowSize);
    });

    getInfo = computed(() => {
        if (debugEl.value && import.meta.client && import.meta.dev) {
            const { bkp, val } = breakpoints.find(({ val }) => window.matchMedia(val).matches) || { bkp: '', val: '' };
            return {
                'window W': windowWidth.value || window.innerWidth,
                bkp: `${bkp}, ${val}`,
                dpr: window.devicePixelRatio,
                imgElemSize: {
                    css: `w:${debugEl.value.offsetWidth} h:${debugEl.value.offsetHeight}`
                },
                imgSize: {
                    css: `w:${imgSize.value.W} h:${imgSize.value.H}`,
                    real: `w:${imgSize.value.rw} h:${imgSize.value.rh}`,
                    prop: `w:${props.variant.find(v => v.breakpoint === bkp)?.width || props.width} h:${props.variant.find(v => v.breakpoint === bkp)?.height || props.height}`
                }
            };
        }

        return null;
    });
}

if (props.preload) {
    useHead({
        link: generateSource().map(({ srcset, media }) => ({
            rel: 'preload',
            as: 'image',
            imagesrcset: srcset,
            media,
            fetchpriority: 'high'
        }))
    });
}

function generateSource() {
    const breakpoints = [
        { variant: props.variant.find(v => v.breakpoint === 'default'), bkp: props.smBkp },
        { variant: props.variant.find(v => v.breakpoint === 'sm'), bkp: props.smBkp },
        { variant: props.variant.find(v => v.breakpoint === 'md'), bkp: props.mdBkp },
        { variant: props.variant.find(v => v.breakpoint === 'lg'), bkp: props.lgBkp },
        { variant: props.variant.find(v => v.breakpoint === 'xl'), bkp: props.xlBkp },
        { variant: props.variant.find(v => v.breakpoint === 'xxl'), bkp: props.xxlBkp }
    ];

    const usedBreakpoints = breakpoints.filter(v => v.variant !== undefined) as {
        variant: AirImgVariant;
        bkp: string;
    }[];
    const firstUsedBreakpoint = usedBreakpoints.find(v => v.variant.breakpoint !== 'default')?.bkp || '';
    //const firstUsedBreakpoint = usedBreakpoints[0]?.bkp || '';

    const sources = usedBreakpoints.map(({ variant, bkp }, i, ar) => {
        const next = ar[i + 1];

        const mediaCondition =
            variant.breakpoint === 'default'
                ? next && next.bkp
                    ? `not all and (min-width: ${next.bkp})` // Si default et next est défini
                    : ''
                : next && next.bkp
                  ? `(min-width: ${bkp}) and (max-width: ${Number(next.bkp.split('px')[0]) - 1}px)` // Si next est défini
                  : `(min-width: ${bkp})`; // Sinon, juste min-width

        return {
            srcset: generateSrcset(variant),
            media: mediaCondition
        };
    });

    // const sources = breakpoints
    //     .filter(({ variant }) => variant?.length)
    //     .map(({ variant, bkp }) => ({
    //         srcset: generateSrcset(variant),
    //         media: `(min-width: ${bkp})`
    //     }));
    if (!usedBreakpoints.find(v => v.variant.breakpoint === 'default'))
        sources.push({
            srcset: generateSrcset({ breakpoint: 'sm' }), // fake breakpoint to get default variant
            media: firstUsedBreakpoint ? `not all and (min-width: ${firstUsedBreakpoint})` : ''
        });

    return sources;
}

function generateSrcset(variant: AirImgVariant) {
    return (
        (variant.dpr || props.dpr)
            .map(dpr => {
                let gravity = Array.isArray(props.gravity) ? `gravity=${props.gravity?.join('x')}` : props.gravity ? `gravity=${props.gravity}` : '';
                gravity = Array.isArray(variant.gravity)
                    ? `gravity=${variant.gravity?.join('x')}`
                    : variant.gravity
                      ? `gravity=${variant.gravity}`
                      : gravity;

                const params = [
                    variant.width || props.width ? `w=${variant.width || props.width}` : '',
                    variant.height || props.height ? `h=${variant.height || props.height}` : '',
                    variant.blur || props.blur ? `blur=${variant.blur || props.blur}` : '',
                    variant.brightness || props.brightness ? `brightness=${variant.brightness || props.brightness}` : '',
                    variant.compression || props.compression ? `compression=${variant.compression || props.compression}` : '',
                    variant.contrast || props.contrast ? `contrast=${variant.contrast || props.contrast}` : '',
                    `dpr=${dpr}`,
                    variant.fit || props.fit ? `fit=${variant.fit || props.fit}` : '',
                    variant.format || props.format ? `format=${variant.format || props.format}` : '',
                    variant.gamma || props.gamma ? `gamma=${variant.gamma || props.gamma}` : '',
                    variant.metadata || props.metadata ? `metadata=${variant.metadata || props.metadata}` : '',
                    variant.onerror || props.onerror ? `onerror=${variant.onerror || props.onerror}` : '',
                    variant.quality || props.quality ? `quality=${variant.quality || props.quality}` : '',
                    variant.rotate || props.rotate ? `rotate=${variant.rotate || props.rotate}` : '',
                    variant.sharpen || props.sharpen ? `sharpen=${variant.sharpen || props.sharpen}` : '',
                    variant.trim || props.trim ? `trim=${variant.trim || props.trim}` : '',
                    gravity
                ]
                    .filter(Boolean)
                    .join(',');

                const url = joinURL('/_icf/image', params, variant.src || props.src || '');
                return variant.dpr || props.dpr ? `${url} ${dpr}x` : url;
            })
            .join(', ') || ''
    );
}
</script>

<style></style>
